import { type Question } from '@/components/admin/questionForm/types';
import {
  isTaskTypeAllowedForModule,
  moduleRequiresTaskType,
} from '@/lib/taskTypeOptions';

export interface ValidationResult {
  errors: Record<string, string>;
  isValid: boolean;
}

// Helper: Validate basic form fields
const validateBasicFormFields = (
  formData: Partial<Question>,
  errors: Record<string, string>,
): void => {
  if (!formData.text?.trim()) {
    errors.text = 'Question text is required';
  }
  if (!formData.skill?.trim()) {
    errors.skill = 'Module/skill is required';
  }
  if (moduleRequiresTaskType(formData.skill)) {
    if (!formData.taskType) {
      errors.taskType = 'Task type is required for writing and speaking';
    } else if (!isTaskTypeAllowedForModule(formData.skill, formData.taskType)) {
      errors.taskType = 'Task type is not valid for this module';
    }
  }
  if (!formData.explanation?.trim()) {
    errors.explanation = 'Explanation is required';
  }
};

// Helper: Validate multiple choice form
const validateMultipleChoiceForm = (
  formData: Partial<Question>,
  errors: Record<string, string>,
): void => {
  const validOptions = (formData.options || []).filter((opt) => opt.trim());
  if (validOptions.length < 2) {
    errors.options = 'At least 2 options are required';
  }

  if (formData.type === 'multiple-choice-single') {
    if (
      !formData.expectedAnswer ||
      !formData.options?.includes(formData.expectedAnswer as string)
    ) {
      errors.expectedAnswer = 'Expected answer must be one of the options';
    }
  } else if (
    !formData.expectedAnswer ||
    (Array.isArray(formData.expectedAnswer) &&
      formData.expectedAnswer.length === 0)
  ) {
    errors.expectedAnswer = 'At least one expected answer is required';
  }
};

// Helper: Validate true/false form
const validateTrueFalseForm = (
  formData: Partial<Question>,
  errors: Record<string, string>,
): void => {
  const normalizedAnswer = formData.expectedAnswer
    ? String(formData.expectedAnswer).toLowerCase()
    : '';
  if (
    !formData.expectedAnswer ||
    !['true', 'false'].includes(normalizedAnswer)
  ) {
    errors.expectedAnswer = 'Expected answer must be True or False';
  }
};

// Helper: Validate text form
const validateTextForm = (
  formData: Partial<Question>,
  errors: Record<string, string>,
): void => {
  if (!formData.expectedAnswer?.toString().trim()) {
    errors.expectedAnswer = 'Sample answer is required';
  }
};

// Helper: Validate audio recording form
const validateAudioRecordingForm = (
  formData: Partial<Question>,
  errors: Record<string, string>,
): void => {
  if (!formData.recordingDuration || formData.recordingDuration <= 0) {
    errors.recordingDuration = 'Recording duration must be greater than 0';
  }
  if (formData.recordingDuration && formData.recordingDuration > 600) {
    errors.recordingDuration = 'Recording duration cannot exceed 600 seconds';
  }
  if (
    formData.preparationTime !== undefined &&
    (formData.preparationTime < 0 || formData.preparationTime > 60)
  ) {
    errors.preparationTime = 'Preparation time must be between 0 and 60 seconds';
  }
};

// Helper: Count blank markers in question text
const countBlankMarkers = (text: string): number => {
  if (!text) return 0;
  const matches = text.match(/___/g);
  return matches ? matches.length : 0;
};

// Helper: Validate fill-in-blank form
const validateFillInBlankForm = (
  formData: Partial<Question>,
  errors: Record<string, string>,
): void => {
  // Validate question text has exactly 5 blank markers
  const blankCount = countBlankMarkers(formData.text || '');
  if (blankCount !== 5) {
    errors.text = `Fill-in-blank questions must have exactly 5 blanks (___) in the question text. Found ${blankCount}.`;
  }

  // Validate expectedAnswer is an array with exactly 5 non-empty strings
  let answers: string[] = [];
  if (Array.isArray(formData.expectedAnswer)) {
    answers = formData.expectedAnswer;
  } else if (typeof formData.expectedAnswer === 'string' && formData.expectedAnswer.trim()) {
    try {
      const parsed = JSON.parse(formData.expectedAnswer);
      answers = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      // If not valid JSON, treat as single answer
      answers = [formData.expectedAnswer];
    }
  }

  if (answers.length !== 5) {
    errors.expectedAnswer = `Fill-in-blank questions require exactly 5 answers. Found ${answers.length}.`;
  } else {
    // Check that all answers are non-empty
    const emptyAnswers = answers.filter((ans) => !ans || !String(ans).trim());
    if (emptyAnswers.length > 0) {
      errors.expectedAnswer = `All 5 answers are required. ${emptyAnswers.length} answer(s) are missing.`;
    }
  }
};

// Helper: Validate form by question type
const validateFormByType = (
  formData: Partial<Question>,
  errors: Record<string, string>,
): void => {
  if (
    formData.type === 'multiple-choice-single' ||
    formData.type === 'multiple-choice-multi'
  ) {
    validateMultipleChoiceForm(formData, errors);
  } else if (formData.type === 'true-false') {
    validateTrueFalseForm(formData, errors);
  } else if (formData.type === 'text-short' || formData.type === 'text-long') {
    validateTextForm(formData, errors);
  } else if (formData.type === 'audio-recording') {
    validateAudioRecordingForm(formData, errors);
  } else if (formData.type === 'live-conversation') {
    // Live-conversation questions require a criteria checklist in expectedAnswer;
    // no recordingDuration (the Realtime bridge handles recording).
    if (!formData.expectedAnswer?.toString().trim()) {
      errors.expectedAnswer = 'A criteria checklist for the expected response is required';
    }
    if (
      formData.preparationTime !== undefined &&
      (formData.preparationTime < 0 || formData.preparationTime > 600)
    ) {
      errors.preparationTime = 'Thinking time must be between 0 and 600 seconds';
    }
  } else if (formData.type === 'fill-in-blank') {
    validateFillInBlankForm(formData, errors);
  }
};

export const validateForm = (
  formData: Partial<Question>,
): ValidationResult => {
  const newErrors: Record<string, string> = {};

  validateBasicFormFields(formData, newErrors);
  validateFormByType(formData, newErrors);

  return {
    errors: newErrors,
    isValid: Object.keys(newErrors).length === 0,
  };
};

export interface SharedParams {
  skill?: string;
  taskType?: string | null;
  examTypes?: string[];
}

// Helper: Validate basic question fields
const validateBasicFields = (
  question: Partial<Question>,
  index: number,
  errors: Record<string, string>,
): boolean => {
  let isValid = true;
  if (!question.text?.trim()) {
    errors[`question_${index}_text`] = `Question ${index + 1}: Text is required`;
    isValid = false;
  }
  if (!question.explanation?.trim()) {
    errors[`question_${index}_explanation`] = `Question ${index + 1}: Explanation is required`;
    isValid = false;
  }
  return isValid;
};

// Helper: Validate audio recording question
const validateAudioRecording = (
  question: Partial<Question>,
  index: number,
  errors: Record<string, string>,
): boolean => {
  let isValid = true;
  if (!question.recordingDuration || question.recordingDuration <= 0) {
    errors[`question_${index}_recordingDuration`] = `Question ${index + 1}: Recording duration is required`;
    isValid = false;
  }
  if (question.recordingDuration && question.recordingDuration > 600) {
    errors[`question_${index}_recordingDuration`] = `Question ${index + 1}: Recording duration cannot exceed 600 seconds`;
    isValid = false;
  }
  if (
    question.preparationTime !== undefined &&
    (question.preparationTime < 0 || question.preparationTime > 60)
  ) {
    errors[`question_${index}_preparationTime`] = `Question ${index + 1}: Preparation time must be between 0 and 60 seconds`;
    isValid = false;
  }
  if (!question.expectedAnswer?.toString().trim()) {
    errors[`question_${index}_expectedAnswer`] = `Question ${index + 1}: Sample answer is required`;
    isValid = false;
  }
  return isValid;
};

// Helper: Validate multiple choice question
const validateMultipleChoice = (
  question: Partial<Question>,
  index: number,
  errors: Record<string, string>,
): boolean => {
  let isValid = true;
  const validOptions = (question.options || []).filter((opt: string) =>
    opt.trim(),
  );
  if (validOptions.length < 2) {
    errors[`question_${index}_options`] = `Question ${index + 1}: At least 2 options are required`;
    isValid = false;
  }
  if (question.type === 'multiple-choice-single') {
    if (
      !question.expectedAnswer ||
      !question.options?.includes(question.expectedAnswer as string)
    ) {
      errors[`question_${index}_expectedAnswer`] = `Question ${index + 1}: Expected answer must be one of the options`;
      isValid = false;
    }
  }
  return isValid;
};

// Helper: Validate true/false question
const validateTrueFalse = (
  question: Partial<Question>,
  index: number,
  errors: Record<string, string>,
): boolean => {
  const normalizedAnswer = question.expectedAnswer
    ? String(question.expectedAnswer).toLowerCase()
    : '';
  if (!question.expectedAnswer || !['true', 'false'].includes(normalizedAnswer)) {
    errors[`question_${index}_expectedAnswer`] = `Question ${index + 1}: Expected answer must be True or False`;
    return false;
  }
  return true;
};

// Helper: Validate text question
const validateTextQuestion = (
  question: Partial<Question>,
  index: number,
  errors: Record<string, string>,
): boolean => {
  if (!question.expectedAnswer?.toString().trim()) {
    errors[`question_${index}_expectedAnswer`] = `Question ${index + 1}: Sample answer is required`;
    return false;
  }
  return true;
};

// Helper: Validate fill-in-blank question
const validateFillInBlank = (
  question: Partial<Question>,
  index: number,
  errors: Record<string, string>,
): boolean => {
  let isValid = true;

  // Validate question text has exactly 5 blank markers
  const blankCount = countBlankMarkers(question.text || '');
  if (blankCount !== 5) {
    errors[`question_${index}_text`] = `Question ${index + 1}: Fill-in-blank questions must have exactly 5 blanks (___) in the question text. Found ${blankCount}.`;
    isValid = false;
  }

  // Validate expectedAnswer is an array with exactly 5 non-empty strings
  let answers: string[] = [];
  if (Array.isArray(question.expectedAnswer)) {
    answers = question.expectedAnswer;
  } else if (typeof question.expectedAnswer === 'string' && question.expectedAnswer.trim()) {
    try {
      const parsed = JSON.parse(question.expectedAnswer);
      answers = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      answers = [question.expectedAnswer];
    }
  }

  if (answers.length !== 5) {
    errors[`question_${index}_expectedAnswer`] = `Question ${index + 1}: Fill-in-blank questions require exactly 5 answers. Found ${answers.length}.`;
    isValid = false;
  } else {
    const emptyAnswers = answers.filter((ans) => !ans || !String(ans).trim());
    if (emptyAnswers.length > 0) {
      errors[`question_${index}_expectedAnswer`] = `Question ${index + 1}: All 5 answers are required. ${emptyAnswers.length} answer(s) are missing.`;
      isValid = false;
    }
  }

  return isValid;
};

// Helper: Validate question by type
const validateQuestionByType = (
  question: Partial<Question>,
  index: number,
  errors: Record<string, string>,
): boolean => {
  if (question.type === 'audio-recording') {
    return validateAudioRecording(question, index, errors);
  }
  if (question.type === 'live-conversation') {
    if (!question.expectedAnswer?.toString().trim()) {
      errors[`question_${index}_expectedAnswer`] = `Question ${index + 1}: A criteria checklist is required`;
      return false;
    }
    return true;
  }
  if (
    question.type === 'multiple-choice-single' ||
    question.type === 'multiple-choice-multi'
  ) {
    return validateMultipleChoice(question, index, errors);
  }
  if (question.type === 'true-false') {
    return validateTrueFalse(question, index, errors);
  }
  if (question.type === 'text-short' || question.type === 'text-long') {
    return validateTextQuestion(question, index, errors);
  }
  if (question.type === 'fill-in-blank') {
    return validateFillInBlank(question, index, errors);
  }
  return true;
};

export const validateAllQuestions = (
  sharedParams: SharedParams,
  questions: Partial<Question>[],
): ValidationResult => {
  let allValid = true;
  const newErrors: Record<string, string> = {};

  // Validate shared parameters
  if (!sharedParams.skill?.trim()) {
    newErrors['shared_skill'] = 'Module/skill is required';
    allValid = false;
  }
  if (moduleRequiresTaskType(sharedParams.skill)) {
    if (!sharedParams.taskType) {
      newErrors['shared_taskType'] = 'Task type is required for writing and speaking';
      allValid = false;
    } else if (!isTaskTypeAllowedForModule(sharedParams.skill, sharedParams.taskType)) {
      newErrors['shared_taskType'] = 'Task type is not valid for this module';
      allValid = false;
    }
  }
  if (!sharedParams.examTypes || sharedParams.examTypes.length === 0) {
    newErrors['shared_examTypes'] = 'At least one exam type is required';
    allValid = false;
  }

  // Validate individual questions
  questions.forEach((q, index) => {
    const basicValid = validateBasicFields(q, index, newErrors);
    const typeValid = validateQuestionByType(q, index, newErrors);
    if (!basicValid || !typeValid) {
      allValid = false;
    }
  });

  return {
    errors: newErrors,
    isValid: allValid,
  };
};
